import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import PdfPageStack from './PdfPageStack.jsx';
import { rectToStyle, styleToRect } from '../utils/pdfRender.js';
import { templatePdfUrl } from '../utils/pdfFormTemplate.js';
import './PdfFieldMapper.css';

/**
 * STAFF FIELD MAPPER
 *
 * Turns a club's uploaded PDF into a fillable form. The server has already read
 * the blanks off the document, so this screen is normally a review: confirm the
 * labels, check what each field means, mark the signature, confirm.
 *
 * It is also the backstop. A scanned form yields no detected fields, so staff can
 * drag boxes straight onto the page — which is why any club's document can be
 * used, not only the tidy ones.
 */

const FIELD_TYPES = ['text', 'date', 'email', 'tel', 'number', 'choice', 'checkbox', 'signature'];

const TYPE_ICON = {
  text: '🔤', date: '📅', email: '✉️', tel: '📞',
  number: '#️⃣', choice: '⭕', checkbox: '☑️', signature: '✍️',
};

/** A fresh field for a box dragged onto an unmapped area. */
const newField = (pageIndex, rect, n) => ({
  page: pageIndex,
  key: `field_${n}`,
  label: `Field ${n}`,
  type: 'text',
  rect,
  // Hand-placed boxes have no printed baseline to sit on, so the stamper falls
  // back to the box bottom.
  fontSize: 10,
  align: 'left',
  required: false,
  source: 'manual',
  confidence: 1,
});

function PdfFieldMapper({ templateId, onClose, onSaved }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [drawing, setDrawing] = useState(null); // { pageIndex, startX, startY, box }
  // Drawing is an explicit mode. On a touch screen the draw layer covers the whole
  // page, so capturing every drag would make a long document impossible to scroll.
  const [addMode, setAddMode] = useState(false);
  const dragRef = useRef(null);

  const authToken = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('ams_user') || '{}')?.token || null;
    } catch {
      return null;
    }
  }, []);

  // ── load map + binding catalog ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [mapRes, catRes] = await Promise.all([
          api.get(`${API_BASE_URL}/api/forms/templates/${templateId}/field-map`),
          api.get(`${API_BASE_URL}/api/forms/bindings`),
        ]);
        if (cancelled) return;
        setMeta(mapRes.data);
        setFields(Array.isArray(mapRes.data.fields) ? mapRes.data.fields : []);
        setCatalog(catRes.data || {});
      } catch (err) {
        if (!cancelled) toast?.error?.(err.response?.data?.error || 'Could not load this form');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const selected = fields.find((f) => f.key === selectedKey) || null;
  const signatureCount = fields.filter((f) => f.type === 'signature').length;
  const boundCount = fields.filter((f) => f.bind).length;
  const lowConfidence = fields.filter((f) => (f.confidence ?? 1) < 0.7);

  const patch = useCallback((key, changes) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, ...changes } : f)));
    setDirty(true);
  }, []);

  const renameKey = (oldKey, rawNext) => {
    const next = String(rawNext || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);
    if (!next) return;
    if (next !== oldKey && fields.some((f) => f.key === next)) {
      toast?.error?.(`"${next}" is already used on this form`);
      return;
    }
    setFields((prev) => prev.map((f) => (f.key === oldKey ? { ...f, key: next } : f)));
    setSelectedKey(next);
    setDirty(true);
  };

  const removeField = (key) => {
    setFields((prev) => prev.filter((f) => f.key !== key));
    if (selectedKey === key) setSelectedKey(null);
    setDirty(true);
  };

  /** Only one field can be the signature; promoting one demotes the rest. */
  const setType = (key, type) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.key === key) return { ...f, type };
        if (type === 'signature' && f.type === 'signature') return { ...f, type: 'text' };
        return f;
      })
    );
    setDirty(true);
  };

  // ── drawing a new box ──
  // Pointer events, not mouse events: one code path covers mouse, finger and
  // Apple Pencil, so the mapper works on the same iPad the kiosk runs on.
  const onDrawPointerDown = (e, pageIndex, cssWidth, cssHeight) => {
    if (!addMode) return;
    if (e.target.closest('.pfm-box')) return; // existing box handles its own drag
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const host = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX - host.left;
    const startY = e.clientY - host.top;
    dragRef.current = { pageIndex, startX, startY, cssWidth, cssHeight };
    setDrawing({ pageIndex, box: { left: startX, top: startY, width: 0, height: 0 } });
  };

  const onDrawPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const host = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - host.left;
    const y = e.clientY - host.top;
    setDrawing({
      pageIndex: d.pageIndex,
      box: {
        left: Math.min(d.startX, x),
        top: Math.min(d.startY, y),
        width: Math.abs(x - d.startX),
        height: Math.abs(y - d.startY),
      },
    });
  };

  const onDrawPointerUp = (e) => {
    const d = dragRef.current;
    const draw = drawing;
    e?.currentTarget?.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
    setDrawing(null);
    if (!d || !draw) return;
    // Ignore stray taps; a real box needs some size.
    if (draw.box.width < 12 || draw.box.height < 6) return;

    const rect = styleToRect(draw.box, d.cssWidth, d.cssHeight);
    const n = fields.length + 1;
    const field = newField(d.pageIndex, rect, n);
    // Scale the default font to the box the human drew.
    field.fontSize = Math.max(6, Math.min(14, rect.h * d.cssHeight * 0.62));
    setFields((prev) => [...prev, field]);
    setSelectedKey(field.key);
    setDirty(true);
    // One box per tap of "Add field" — stops a stray drag creating extras.
    setAddMode(false);
  };

  // ── moving / resizing an existing box by pointer ──
  const boxDragRef = useRef(null);
  const onBoxPointerDown = (e, field, mode, cssWidth, cssHeight) => {
    e.stopPropagation();
    setSelectedKey(field.key);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    boxDragRef.current = {
      key: field.key,
      mode, // 'move' | 'resize'
      startX: e.clientX,
      startY: e.clientY,
      rect: { ...field.rect },
      cssWidth,
      cssHeight,
    };
  };

  const onBoxPointerMove = (e) => {
    const d = boxDragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.cssWidth;
    const dy = (e.clientY - d.startY) / d.cssHeight;
    const r = { ...d.rect };
    if (d.mode === 'move') {
      r.x = Math.max(0, Math.min(1 - r.w, d.rect.x + dx));
      r.y = Math.max(0, Math.min(1 - r.h, d.rect.y + dy));
    } else {
      r.w = Math.max(0.01, Math.min(1 - r.x, d.rect.w + dx));
      r.h = Math.max(0.005, Math.min(1 - r.y, d.rect.h + dy));
    }
    setFields((prev) => prev.map((f) => (f.key === d.key ? { ...f, rect: r } : f)));
  };

  const onBoxPointerUp = (e) => {
    if (!boxDragRef.current) return;
    e?.currentTarget?.releasePointerCapture?.(e.pointerId);
    boxDragRef.current = null;
    setDirty(true);
  };

  // ── nudge / resize the selected box from the sidebar ──
  const nudge = (axis, delta) => {
    if (!selected) return;
    const r = { ...selected.rect };
    if (axis === 'x') r.x = Math.max(0, Math.min(1 - r.w, r.x + delta));
    if (axis === 'y') r.y = Math.max(0, Math.min(1 - r.h, r.y + delta));
    if (axis === 'w') r.w = Math.max(0.01, Math.min(1 - r.x, r.w + delta));
    if (axis === 'h') r.h = Math.max(0.005, Math.min(1 - r.y, r.h + delta));
    patch(selected.key, { rect: r });
  };

  // ── save ──
  const save = async (confirm) => {
    if (confirm && signatureCount === 0) {
      toast?.error?.('Mark one field as the signature before confirming');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`${API_BASE_URL}/api/forms/templates/${templateId}/field-map`, {
        fields,
        confirm: !!confirm,
      });
      setDirty(false);
      setMeta((m) => ({ ...m, status: res.data.status }));
      toast?.success?.(res.data.message);
      onSaved?.(res.data);
      if (confirm) onClose?.();
    } catch (err) {
      const data = err.response?.data;
      toast?.error?.(data?.problems?.[0] || data?.error || 'Could not save the field map');
    } finally {
      setSaving(false);
    }
  };

  const redetect = async () => {
    if (!window.confirm('Re-read the blanks from the PDF? Your current mapping will be replaced.')) return;
    setSaving(true);
    try {
      const res = await api.post(`${API_BASE_URL}/api/forms/templates/${templateId}/redetect`);
      setFields(res.data.fields || []);
      setSelectedKey(null);
      setDirty(true);
      toast?.success?.(`Found ${res.data.fields?.length || 0} field(s)`);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Re-detection failed');
    } finally {
      setSaving(false);
    }
  };

  const pdfUrl = templatePdfUrl({ pdf_file_path: meta?.pdfPath }, API_BASE_URL);

  const overlay = ({ pageIndex, cssWidth, cssHeight }) => (
    <div
      className={`pfm-drawlayer ${addMode ? 'adding' : ''}`}
      onPointerDown={(e) => onDrawPointerDown(e, pageIndex, cssWidth, cssHeight)}
      onPointerMove={onDrawPointerMove}
      onPointerUp={onDrawPointerUp}
      onPointerCancel={onDrawPointerUp}
    >
      {fields
        .filter((f) => (f.page || 0) === pageIndex)
        .map((f) => {
          const isSel = f.key === selectedKey;
          const cls = [
            'pfm-box',
            `t-${f.type}`,
            isSel ? 'sel' : '',
            (f.confidence ?? 1) < 0.7 ? 'lowconf' : '',
            f.bind ? 'bound' : 'unbound',
          ].join(' ');
          return (
            <div
              key={f.key}
              className={cls}
              style={rectToStyle(f.rect, cssWidth, cssHeight)}
              onPointerDown={(e) => onBoxPointerDown(e, f, 'move', cssWidth, cssHeight)}
              onPointerMove={onBoxPointerMove}
              onPointerUp={onBoxPointerUp}
              onPointerCancel={onBoxPointerUp}
              title={`${f.label} (${f.type})${f.bind ? ` → ${f.bind}` : ''}`}
            >
              <span className="pfm-box-tag">
                {TYPE_ICON[f.type] || '🔤'} {f.label}
              </span>
              {/* Corner handle, sized for a fingertip rather than a cursor. */}
              {isSel && (
                <span
                  className="pfm-box-handle"
                  onPointerDown={(e) => onBoxPointerDown(e, f, 'resize', cssWidth, cssHeight)}
                  onPointerMove={onBoxPointerMove}
                  onPointerUp={onBoxPointerUp}
                  onPointerCancel={onBoxPointerUp}
                  title="Drag to resize"
                />
              )}
            </div>
          );
        })}

      {/* choice options draw their own circles so staff can see what gets ringed */}
      {fields
        .filter((f) => (f.page || 0) === pageIndex && f.type === 'choice' && Array.isArray(f.options))
        .flatMap((f) =>
          f.options.map((o, i) => (
            <div
              key={`${f.key}-opt-${i}`}
              className="pfm-optbox"
              style={rectToStyle(o.rect, cssWidth, cssHeight)}
              title={`${f.label}: ${o.value}`}
            />
          ))
        )}

      {drawing && drawing.pageIndex === pageIndex && (
        <div className="pfm-drawbox" style={{ left: drawing.box.left, top: drawing.box.top, width: drawing.box.width, height: drawing.box.height }} />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="pfm-modal">
        <div className="pfm-shell"><p className="pfm-loading">Loading form…</p></div>
      </div>
    );
  }

  return (
    <div className="pfm-modal">
      <div className="pfm-shell">
        {/* ── header ── */}
        <div className="pfm-head">
          <div>
            <h2>Map fields — {meta?.formName}</h2>
            <p className="pfm-sub">
              {fields.length} field{fields.length === 1 ? '' : 's'} · {boundCount} linked to athlete data ·{' '}
              <span className={`pfm-status s-${meta?.status}`}>
                {meta?.status === 'confirmed' ? 'Confirmed — live for parents' : 'Not yet confirmed'}
              </span>
            </p>
          </div>
          <div className="pfm-head-actions">
            <button
              className={addMode ? 'pfm-btn-primary' : 'pfm-btn-ghost'}
              onClick={() => setAddMode((v) => !v)}
              disabled={saving}
            >
              {addMode ? '✏️ Drawing — tap to cancel' : '➕ Add field'}
            </button>
            <button className="pfm-btn-ghost" onClick={redetect} disabled={saving}>Re-detect</button>
            <button className="pfm-btn-ghost" onClick={() => save(false)} disabled={saving || !dirty}>
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button className="pfm-btn-primary" onClick={() => save(true)} disabled={saving}>
              Confirm form
            </button>
            <button className="pfm-btn-close" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        {/* ── guidance that actually matters ── */}
        {signatureCount === 0 && (
          <div className="pfm-warn">
            ⚠️ No signature field yet. Select the form's signature line and set its type to
            <strong> ✍️ signature</strong> — parents cannot sign until you do.
          </div>
        )}
        {signatureCount > 1 && (
          <div className="pfm-warn">⚠️ More than one signature field; only one is supported.</div>
        )}
        {fields.length === 0 && (
          <div className="pfm-warn">
            This form has no detected blanks — likely a scan. <strong>Drag a box</strong> onto each
            line that needs filling in.
          </div>
        )}
        {lowConfidence.length > 0 && (
          <div className="pfm-note">
            {lowConfidence.length} field{lowConfidence.length === 1 ? '' : 's'} detected with low
            confidence (outlined in amber) — worth a check.
          </div>
        )}

        <div className="pfm-body">
          {/* ── the page, with boxes on it ── */}
          <div className="pfm-canvas">
            {pdfUrl ? (
              <PdfPageStack url={pdfUrl} authToken={authToken} renderOverlay={overlay} maxWidth={820} />
            ) : (
              <p className="pfm-loading">This template has no PDF on file.</p>
            )}
            <p className="pfm-hint">
              {addMode
                ? 'Drag a box over the blank you want to add.'
                : 'Tap a box to edit it, drag it to move, or use ➕ Add field to draw a new one.'}
            </p>
          </div>

          {/* ── inspector ── */}
          <aside className="pfm-side">
            {selected ? (
              <div className="pfm-inspector">
                <h3>Selected field</h3>

                <label className="pfm-f">
                  <span>Label (what the parent sees)</span>
                  <input
                    value={selected.label || ''}
                    onChange={(e) => patch(selected.key, { label: e.target.value })}
                  />
                </label>

                <label className="pfm-f">
                  <span>Key (stored name)</span>
                  <input
                    defaultValue={selected.key}
                    key={selected.key}
                    onBlur={(e) => renameKey(selected.key, e.target.value)}
                  />
                </label>

                <label className="pfm-f">
                  <span>Type</span>
                  <select value={selected.type} onChange={(e) => setType(selected.key, e.target.value)}>
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>{TYPE_ICON[t]} {t}</option>
                    ))}
                  </select>
                </label>

                <label className="pfm-f">
                  <span>
                    What is this? <em>links to athlete data — prefills and saves back</em>
                  </span>
                  <select
                    value={selected.bind || ''}
                    onChange={(e) => patch(selected.key, { bind: e.target.value || undefined })}
                  >
                    <option value="">— not linked (saved with the form only) —</option>
                    {Object.entries(catalog).map(([group, items]) => (
                      <optgroup key={group} label={group}>
                        {items.map((it) => (
                          <option key={it.key} value={it.key}>
                            {it.label}{it.writes ? '' : ' (read-only)'}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>

                {selected.type === 'choice' && (
                  <div className="pfm-opts">
                    <span className="pfm-opts-title">Options (circled on the form)</span>
                    {(selected.options || []).map((o, i) => (
                      <input
                        key={i}
                        value={o.value}
                        onChange={(e) => {
                          const options = [...selected.options];
                          options[i] = { ...options[i], value: e.target.value };
                          patch(selected.key, { options });
                        }}
                      />
                    ))}
                    {!selected.options?.length && (
                      <p className="pfm-opts-empty">
                        No options detected — this cannot be a choice field without them.
                      </p>
                    )}
                  </div>
                )}

                <label className="pfm-check">
                  <input
                    type="checkbox"
                    checked={!!selected.required}
                    onChange={(e) => patch(selected.key, { required: e.target.checked })}
                  />
                  Required before submitting
                </label>

                <div className="pfm-nudge">
                  <span>Position</span>
                  <div className="pfm-nudge-grid">
                    <button onClick={() => nudge('y', -0.002)} title="Up">↑</button>
                    <button onClick={() => nudge('y', 0.002)} title="Down">↓</button>
                    <button onClick={() => nudge('x', -0.002)} title="Left">←</button>
                    <button onClick={() => nudge('x', 0.002)} title="Right">→</button>
                    <button onClick={() => nudge('w', -0.004)} title="Narrower">↔−</button>
                    <button onClick={() => nudge('w', 0.004)} title="Wider">↔+</button>
                    <button onClick={() => nudge('h', -0.002)} title="Shorter">↕−</button>
                    <button onClick={() => nudge('h', 0.002)} title="Taller">↕+</button>
                  </div>
                </div>

                <button className="pfm-btn-danger" onClick={() => removeField(selected.key)}>
                  Remove this field
                </button>
              </div>
            ) : (
              <div className="pfm-empty">
                <p>Select a field on the page to edit it, or drag a new box.</p>
              </div>
            )}

            {/* ── full list, so nothing hides off-screen ── */}
            <div className="pfm-list">
              <h3>All fields</h3>
              {fields.length === 0 && <p className="pfm-list-empty">None yet.</p>}
              <ul>
                {fields.map((f) => (
                  <li
                    key={f.key}
                    className={`${f.key === selectedKey ? 'sel' : ''} ${f.bind ? '' : 'unbound'}`}
                    onClick={() => setSelectedKey(f.key)}
                  >
                    <span className="pfm-li-type">{TYPE_ICON[f.type] || '🔤'}</span>
                    <span className="pfm-li-label">{f.label}</span>
                    {f.page > 0 && <span className="pfm-li-page">p{f.page + 1}</span>}
                    {!f.bind && <span className="pfm-li-warn" title="Not linked to athlete data">↯</span>}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default PdfFieldMapper;
